import { Overlay, RepositionScrollStrategy } from '@angular/cdk/overlay';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, NgModule, Provider } from '@angular/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  MAT_SELECT_SCROLL_STRATEGY,
  MatSelectModule,
} from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { NgApiDocumentationModule } from '@pishgamrayan/ng-api-documentation';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

import { LibNgOnlineSurveyModule } from '@pishgamrayan/ng-online-survey';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthCallbackComponent } from './auth-callback/auth-callback.component';
import { LocalStorageKeys } from './configs/local-storage-keys-config';
import { ApiConfiguration } from './core/api/api-configuration';

import { appGraphqlProvider } from './core/graphql/graphql.provider';
import { AuthGuard } from './core/guards/auth.guard';
import { ClientDataGuard } from './core/guards/client-data.guard';
import { HttpActiveOrganizationInterceptor } from './core/interceptors/active-organization.Interceptor';
import { HttpCheckRequestInterceptorProviders } from './core/interceptors/check-request.Interceptor';
import { HttpCultureInterceptorProviders } from './core/interceptors/culture.Interceptor';
import { HttpErrorInterceptorProviders } from './core/interceptors/error.Interceptor';
import { HttpLoadingInterceptorProviders } from './core/interceptors/loading.Interceptor';
import { HttpLoginInterceptorProviders } from './core/interceptors/login.Interceptor';
import { HttpPermissionInterceptorProviders } from './core/interceptors/permission.Interceptor';
import { HttpPreventDuplicateRequestInterceptorProviders } from './core/interceptors/prevent-duplicate-requests.Interceptor';
import { HttpServerDateTimeInterceptorProviders } from './core/interceptors/server-date-time.Interceptor';
import { HttpTokenInterceptorProviders } from './core/interceptors/token.Interceptor';
import { ClientDataInitializerService } from './core/services/authentication/client-data-initializer.service';
import { GetApiUrlInitializerService } from './core/services/authentication/get-api-url-initializer.service';
import { AppConfigService } from './core/services/config.service';
import { AppIntroComponent } from './layout/components/app-intro/app-intro.component';
import { StartupErrorComponent } from './layout/components/startup-error/startup-error.component';
import { StartupLoadingComponent } from './layout/components/startup-loading/startup-loading.component';
import { SelectSearchComponent } from './shared/components/form-kit/select-search/select-search.component';
import { ShareModule } from './shared/share.module';

export function scrollFactory(
  overlay: Overlay
): () => RepositionScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

/**initializer provider for GET API URL and GET DOMAIN DATA and CHECK SYSTEM STATUS   */
export const INIT_APP_CONFIGURATION: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (
    getApiDataService: GetApiUrlInitializerService,
    apiConfigService: ApiConfiguration,
    getCurrentClientDataService: ClientDataInitializerService
  ) => {
    return () => {
      return getApiDataService.init().then(() => {
        let apiUrl: string = '';
        const localApiUrl: string = localStorage.getItem(
          LocalStorageKeys.apiUrl
        );
        const config = AppConfigService.config;
        if (!config.production && localApiUrl) apiUrl = localApiUrl;
        else apiUrl = config.apiUrl;
        if (apiUrl) {
          apiConfigService.rootUrl = apiUrl;
          return getCurrentClientDataService.init(); //get domain data
        } else return new Promise<Boolean>(() => {}); //no connection to api
      });
    };
  },
  deps: [
    GetApiUrlInitializerService,
    ApiConfiguration,
    ClientDataInitializerService,
    AppConfigService,
  ],
  multi: true,
};

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    MatSelectModule,
    NgxMatSelectSearchModule,
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(),
    InlineSVGModule.forRoot(),
    MonacoEditorModule.forRoot(),
    AppIntroComponent,
    AppRoutingModule,
    NgbModule,
    MatDialogModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    ShareModule,
    StartupErrorComponent,
    StartupLoadingComponent,
    SelectSearchComponent,
    AuthCallbackComponent,
    MonacoEditorModule.forRoot({}),
    NgApiDocumentationModule.forRoot({
      apiUrl: 'https://smspanel.pishgamrayan.com',
      projectName: 'smsPanel',
      homePageUrl: '/',
      pageRoute: 'documentation',
      logoPath: '/assets/media/logos/logo-with-bg.svg',
    }),
    LibNgOnlineSurveyModule.forRoot({
      apiUrl: 'https://smspanel.pishgamrayan.com/OnlineSurvey',
    }),
    //service worker
    // ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production, registrationStrategy: 'registerImmediately' })
  ],
  providers: [
    [INIT_APP_CONFIGURATION],
    ClientDataGuard,
    AuthGuard,
    provideHighlightOptions({
      fullLibraryLoader: () => import('highlight.js'),
    }),
    provideHttpClient(),
    HttpCheckRequestInterceptorProviders, //check request
    HttpServerDateTimeInterceptorProviders, //get serverDateTime
    HttpTokenInterceptorProviders, // check auth and handle refresh token
    HttpLoginInterceptorProviders, // for save captcha cookies
    HttpCultureInterceptorProviders, // adds params to requests for multi lang
    HttpActiveOrganizationInterceptor, //active oran
    HttpLoadingInterceptorProviders, // global loading
    HttpPermissionInterceptorProviders, //handle impersonate
    HttpErrorInterceptorProviders, //handle response errors
    HttpPreventDuplicateRequestInterceptorProviders, //handle duplicate requests
    appGraphqlProvider,
    { provide: MatDialogRef, useValue: {} },
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
